import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { FiClock, FiArrowRight } from "react-icons/fi";
import axios from "axios";
import newsPosts from "../data/newsPosts";

const CMS_URL =
  "https://xpandcms.azurewebsites.net/api/content/67a07f64239c7c8af4a74235";
const CMS_TOKEN =
  "0bae8bbc32e2535ff2488a63a39ef14a2d6177efb2c3efe79fd46f092044112c";
const CACHE_EXPIRATION_TIME = 60 * 60 * 1000;

const readCache = () => {
  try {
    const data = JSON.parse(localStorage.getItem("blogPosts"));
    const timestamp = Number(localStorage.getItem("blogPostsTimestamp"));
    return { data, fresh: data && Date.now() - timestamp < CACHE_EXPIRATION_TIME };
  } catch {
    return { data: null, fresh: false };
  }
};

const excerpt = (html, words = 22) => {
  const text = html.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
  const parts = text.split(" ");
  return parts.length > words ? parts.slice(0, words).join(" ") + "…" : text;
};

const LatestNews = () => {
  const [cmsPosts, setCmsPosts] = useState(() => readCache().data || []);

  useEffect(() => {
    if (readCache().fresh) return;
    axios
      .get(CMS_URL, { headers: { Authorization: `Bearer ${CMS_TOKEN}` } })
      .then((response) => {
        const data = response.data.post || [];
        setCmsPosts(data);
        try {
          localStorage.setItem("blogPosts", JSON.stringify(data));
          localStorage.setItem("blogPostsTimestamp", Date.now().toString());
        } catch {}
      })
      .catch((error) => console.error("Error fetching blog posts:", error));
  }, []);

  const latest = useMemo(
    () =>
      [...newsPosts, ...cmsPosts]
        .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
        .slice(0, 3),
    [cmsPosts]
  );

  if (latest.length === 0) return null;

  return (
    <section className="py-16 px-4 bg-blue-50">
      <div className="max-w-6xl mx-auto">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-10">
          <div>
            <h2 className="text-3xl md:text-4xl font-extrabold text-gray-900">
              Latest News
            </h2>
            <p className="text-gray-600 mt-2">
              What's happening at Ikusasa Technology Solutions.
            </p>
          </div>
          <Link
            to="/blog"
            className="inline-flex items-center gap-2 text-blue-600 font-semibold hover:underline"
          >
            View all news <FiArrowRight />
          </Link>
        </div>

        <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
          {latest.map((post) => (
            <Link
              key={post.id}
              to={`/blog/${post.slug}`}
              className="group bg-white rounded-xl shadow-md overflow-hidden flex flex-col transition duration-300 hover:shadow-xl hover:-translate-y-1"
            >
              <img
                src={post.featuredImage}
                alt={post.title}
                className="w-full h-52 object-cover"
                loading="lazy"
              />
              <div className="p-6 flex flex-col flex-1">
                <div className="flex items-center text-gray-500 text-sm mb-3">
                  <FiClock className="mr-1" />
                  {new Date(post.createdAt).toLocaleDateString("en-GB", {
                    day: "2-digit",
                    month: "short",
                    year: "numeric",
                  })}
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3 group-hover:text-blue-600">
                  {post.title}
                </h3>
                <p className="text-gray-700 mb-4 flex-1">{excerpt(post.content || "")}</p>
                <span className="inline-flex items-center gap-2 text-blue-600 font-medium">
                  Read more <FiArrowRight />
                </span>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
};

export default LatestNews;
